/**
 * Vorgesetzten-Inbox für Weiterbildungs-/Qualifikations-Anträge.
 * Listet alle „pending" Anträge der Mitarbeiter:innen, für die der/die User:in
 * zuständig ist. Pro Antrag: Inline-Edit (Tippfehler), Freigeben, Ablehnen.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { DatePickerDE } from '../components/ui/date-picker-de';
import { Loader2, CheckCircle2, XCircle, Pencil, FileText, ExternalLink, GraduationCap, Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import { qualificationProposalsAPI } from '../lib/api';
import { formatDate } from './profile/profileHelpers';
import { openAuthFile } from '../lib/openAuthFile';

const ProposalCard = ({ proposal, onEdit, onApprove, onReject }) => {
  const data = proposal.payload || {};
  const name = data.name || data.title || (proposal.action === 'delete' ? 'Eintrag löschen' : 'Neuer Eintrag');
  const kindLabel = proposal.kind === 'training' ? 'Weiterbildung' : 'Qualifikation';
  const actionLabel = { create: 'Neuer Eintrag', update: 'Änderung', delete: 'Löschung' }[proposal.action];
  const actionColor = { create: 'bg-blue-100 text-blue-800 border-blue-200',
                        update: 'bg-amber-100 text-amber-800 border-amber-200',
                        delete: 'bg-red-100 text-red-800 border-red-200' }[proposal.action];

  return (
    <Card className="border-border/60" data-testid={`proposal-card-${proposal.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <GraduationCap className="h-4 w-4 text-primary shrink-0" />
              <p className="font-medium">{name}</p>
              <Badge variant="outline" className={actionColor + ' text-xs'}>{actionLabel}</Badge>
              <Badge variant="outline" className="text-xs">{kindLabel}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              <strong>{proposal.employee_name}</strong> · eingereicht {formatDate(proposal.created_at)}
            </p>
          </div>
        </div>

        {/* Payload anzeigen */}
        <div className="rounded-md bg-muted/40 p-3 space-y-1 text-sm">
          {data.provider && <p><span className="text-muted-foreground">Anbieter:</span> {data.provider}</p>}
          {data.institution && <p><span className="text-muted-foreground">Institution:</span> {data.institution}</p>}
          {(data.completed_date || data.date) && (
            <p><span className="text-muted-foreground">{proposal.kind === 'training' ? 'Abgeschlossen' : 'Erworben'}:</span> {formatDate(data.completed_date || data.date)}</p>
          )}
          {data.expiry_date && <p><span className="text-muted-foreground">Gültig bis:</span> {formatDate(data.expiry_date)}</p>}
          {data.hours != null && data.hours !== '' && <p><span className="text-muted-foreground">Umfang:</span> {data.hours} h</p>}
          {data.description && <p className="italic">„{data.description}"</p>}
        </div>

        {proposal.reason && (
          <p className="text-sm text-muted-foreground italic border-l-2 pl-3">„{proposal.reason}"</p>
        )}

        {proposal.document_filename && (
          <button
            type="button"
            onClick={() => openAuthFile(proposal.document_url, { filename: proposal.document_filename })
              .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))}
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <FileText className="h-4 w-4" /> {proposal.document_filename}
            <ExternalLink className="h-3 w-3" />
          </button>
        )}

        {/* Aktionen */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button size="sm" variant="outline" onClick={() => onEdit(proposal)} data-testid={`proposal-edit-${proposal.id}`}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
          </Button>
          <Button size="sm" onClick={() => onApprove(proposal)} data-testid={`proposal-approve-${proposal.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Freigeben
          </Button>
          <Button size="sm" variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => onReject(proposal)} data-testid={`proposal-reject-${proposal.id}`}>
            <XCircle className="h-3.5 w-3.5 mr-1" /> Ablehnen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EditDialog = ({ open, onOpenChange, proposal, onSaved }) => {
  const [payload, setPayload] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && proposal) setPayload({ ...(proposal.payload || {}) });
  }, [open, proposal]);

  if (!proposal) return null;
  const fields = proposal.kind === 'training'
    ? [
        { key: 'name', label: 'Titel' },
        { key: 'provider', label: 'Anbieter' },
        { key: 'completed_date', label: 'Abgeschlossen', type: 'date' },
        { key: 'expiry_date', label: 'Gültig bis', type: 'date' },
        { key: 'hours', label: 'Stunden', type: 'number' },
        { key: 'description', label: 'Beschreibung', type: 'textarea' },
      ]
    : [
        { key: 'name', label: 'Bezeichnung' },
        { key: 'institution', label: 'Institution' },
        { key: 'date', label: 'Erworben', type: 'date' },
        { key: 'expiry_date', label: 'Gültig bis', type: 'date' },
      ];

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = { ...payload };
      if (cleaned.hours !== undefined && cleaned.hours !== '') cleaned.hours = Number(cleaned.hours);
      await qualificationProposalsAPI.edit(proposal.id, { payload: cleaned });
      toast.success('Antrag korrigiert');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Antrag bearbeiten</DialogTitle>
          <DialogDescription>Korrigiere die Werte des Antrags. Nach Speichern bleibt der Antrag weiterhin im Status „wartet auf Freigabe".</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.key} className="space-y-1">
              <Label>{f.label}</Label>
              {f.type === 'date' ? (
                <DatePickerDE value={payload[f.key] || ''} onChange={(v) => setPayload(p => ({ ...p, [f.key]: v }))} />
              ) : f.type === 'textarea' ? (
                <Textarea value={payload[f.key] || ''} onChange={(e) => setPayload(p => ({ ...p, [f.key]: e.target.value }))} rows={2} />
              ) : (
                <Input type={f.type || 'text'} value={payload[f.key] ?? ''} onChange={(e) => setPayload(p => ({ ...p, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="proposal-edit-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectDialog = ({ open, onOpenChange, proposal, onRejected }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Bitte einen Grund angeben');
      return;
    }
    setSaving(true);
    try {
      await qualificationProposalsAPI.reject(proposal.id, reason.trim());
      toast.success('Antrag abgelehnt');
      onOpenChange(false);
      onRejected?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Antrag ablehnen</DialogTitle>
          <DialogDescription>Der/die Mitarbeiter:in wird mit deinem Grund benachrichtigt.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Grund der Ablehnung" rows={3} data-testid="reject-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving} className="bg-destructive hover:bg-destructive/90" data-testid="reject-confirm">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Ablehnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function QualificationProposalsInbox() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProposal, setEditProposal] = useState(null);
  const [rejectProposal, setRejectProposal] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qualificationProposalsAPI.inbox(tab);
      setItems(res.data);
    } catch (e) {
      toast.error('Konnte Inbox nicht laden');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { reload(); }, [reload]);

  const approve = async (p) => {
    if (!window.confirm(`Antrag von ${p.employee_name} wirklich freigeben?`)) return;
    try {
      await qualificationProposalsAPI.approve(p.id);
      toast.success('Antrag freigegeben');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Freigabe fehlgeschlagen');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-primary" />
            Qualifikations-Anträge
          </h1>
          <p className="text-muted-foreground">Weiterbildungen und Qualifikationen freigeben, bearbeiten oder ablehnen</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Hourglass className="h-4 w-4 mr-1" /> Offen
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Freigegeben
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">
              <XCircle className="h-4 w-4 mr-1" /> Abgelehnt
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="space-y-3 mt-4">
            {loading ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Lade …</CardContent></Card>
            ) : items.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Keine Anträge in diesem Status</CardContent></Card>
            ) : (
              items.map(p => (
                <ProposalCard
                  key={p.id}
                  proposal={p}
                  onEdit={setEditProposal}
                  onApprove={approve}
                  onReject={setRejectProposal}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      <EditDialog
        open={!!editProposal}
        onOpenChange={(o) => { if (!o) setEditProposal(null); }}
        proposal={editProposal}
        onSaved={reload}
      />
      <RejectDialog
        open={!!rejectProposal}
        onOpenChange={(o) => { if (!o) setRejectProposal(null); }}
        proposal={rejectProposal}
        onRejected={reload}
      />
    </DashboardLayout>
  );
}
